import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { errorToast, successToast } from '../../../utils/helper';
import {
  createGameAchievement,
  updateGameAchievement,
  getAchievementTypes
} from '../../../api/achievementApi';
import Spinner from '../Spinner';

// Achievement Icons Enum
const ACHIEVEMENT_ICONS = [
  {
    title: 'Badge 1',
    value: 'badge_1',
    path: require('../../../assets/images/achievement/badge 1.png')
  },
  {
    title: 'Badge 2',
    value: 'badge_2',
    path: require('../../../assets/images/achievement/badge 2.png')
  },
  {
    title: 'Badge 3',
    value: 'badge_3',
    path: require('../../../assets/images/achievement/badge 3.png')
  },
  {
    title: 'Badge 4',
    value: 'badge_4',
    path: require('../../../assets/images/achievement/badge 4.png')
  },
  {
    title: 'Badge 5',
    value: 'badge_5',
    path: require('../../../assets/images/achievement/badge 5.png')
  },
  {
    title: 'Badge 6',
    value: 'badge_6',
    path: require('../../../assets/images/achievement/badge 6.png')
  },
  {
    title: 'Badge 7',
    value: 'badge_7',
    path: require('../../../assets/images/achievement/badge 7.png')
  },
  {
    title: 'Badge 8',
    value: 'badge_8',
    path: require('../../../assets/images/achievement/badge 8.png')
  },
  {
    title: 'Badge 9',
    value: 'badge_9',
    path: require('../../../assets/images/achievement/badge 9.png')
  },
  {
    title: 'Badge 10',
    value: 'badge_10',
    path: require('../../../assets/images/achievement/badge 10.png')
  },
  {
    title: 'Badge 11',
    value: 'badge_11',
    path: require('../../../assets/images/achievement/badge 11.png')
  }
];

const CreateAchievementModal = ({ open, setOpen, onSuccess, onClose, editData }) => {
  const [loader, setLoader] = useState(false);
  const [types, setTypes] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '',
    type: '',
    targetValue: '',
    rewards: ''
  });
  const [errors, setErrors] = useState({
    title: '',
    description: '',
    type: '',
    targetValue: '',
    rewards: ''
  });

  useEffect(() => {
    if (open) {
      fetchAchievementTypes();
      // Populate form with edit data if provided
      if (editData) {
        setFormData({
          title: editData.title || '',
          description: editData.description || '',
          icon: editData.icon || '',
          type: editData.type?.toString() || '',
          targetValue: editData.targetValue?.toString() || '',
          rewards: editData.rewards?.toString() || ''
        });
      } else {
        resetForm();
      }
    }
  }, [open, editData]);

  const fetchAchievementTypes = async () => {
    try {
      const response = await getAchievementTypes();
      console.log('Achievement Types Response:', response);
      if (response?.data) {
        setTypes(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching achievement types:', error);
      errorToast('Failed to load achievement types');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    // Handle file input for icon
    if (type === 'file' && files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          [name]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleIconSelect = (iconValue) => {
    setFormData((prev) => ({
      ...prev,
      icon: iconValue
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      icon: '',
      type: '',
      targetValue: '',
      rewards: ''
    });
    setErrors({
      title: '',
      description: '',
      type: '',
      targetValue: '',
      rewards: ''
    });
  };

  const handleClose = () => {
    resetForm();
    setOpen(false);
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {
      title: '',
      description: '',
      type: '',
      targetValue: '',
      rewards: ''
    };

    if (!formData.title.trim()) {
      newErrors.title = 'Please enter a title';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Please enter a description';
    }
    if (!formData.type) {
      newErrors.type = 'Please select an achievement type';
    }
    if (!formData.targetValue || formData.targetValue <= 0) {
      newErrors.targetValue = 'Please enter a valid target value';
    }
    if (!formData.rewards || formData.rewards <= 0) {
      newErrors.rewards = 'Please enter valid rewards';
    }

    setErrors(newErrors);

    // If there are any errors, don't submit
    if (Object.values(newErrors).some((error) => error !== '')) {
      return;
    }

    setLoader(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        icon: formData.icon || 'icon_url',
        type: parseInt(formData.type),
        targetValue: parseInt(formData.targetValue),
        rewards: parseInt(formData.rewards)
      };

      let response;
      if (editData && editData._id) {
        // Update existing achievement
        response = await updateGameAchievement(editData._id, payload);
        console.log('Update Achievement Response:', response);
      } else {
        // Create new achievement
        response = await createGameAchievement(payload);
        console.log('Create Achievement Response:', response);
      }

      if (response) {
        successToast(
          editData ? 'Achievement updated successfully' : 'Achievement created successfully'
        );
        resetForm();
        setOpen(false);
        onSuccess?.();
      }
    } catch (error) {
      console.error('Error saving achievement:', error);
      errorToast(error?.message || `Failed to ${editData ? 'update' : 'create'} achievement`);
    } finally {
      setLoader(false);
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-shoorah-darkBgTabColor text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-200 dark:border-shoorah-darkBorder">
                  <DialogTitle
                    as="h3"
                    className="text-xl font-semibold text-gray-900 dark:text-white"
                  >
                    {editData ? 'Edit Achievement' : 'Create Achievement'}
                  </DialogTitle>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={loader}
                    className="rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none disabled:opacity-50"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <div className="px-6 py-6 max-h-[calc(100vh-250px)] overflow-y-auto">
                    <div className="grid grid-cols-1 gap-6">
                      {/* Title - Full Width */}
                      <div>
                        <label
                          htmlFor="title"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                          Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          disabled={loader}
                          className={`w-full px-4 py-2 border ${
                            errors.title ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          } rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent dark:bg-shoorah-darkBgColor dark:text-white disabled:opacity-50`}
                          placeholder="Enter achievement title"
                        />
                        {errors.title && (
                          <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                        )}
                      </div>

                      {/* Description - Full Width, Textarea with 3 rows */}
                      <div>
                        <label
                          htmlFor="description"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                          Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="description"
                          name="description"
                          rows="3"
                          value={formData.description}
                          onChange={handleInputChange}
                          disabled={loader}
                          className={`w-full px-4 py-2 border ${
                            errors.description
                              ? 'border-red-500'
                              : 'border-gray-300 dark:border-gray-600'
                          } rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent dark:bg-shoorah-darkBgColor dark:text-white disabled:opacity-50 resize-none`}
                          placeholder="Enter achievement description"
                        />
                        {errors.description && (
                          <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                        )}
                      </div>

                      {/* Achievement Type - Full Width */}
                      <div>
                        <label
                          htmlFor="type"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                          Achievement Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="type"
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                          disabled={loader}
                          className={`w-full px-4 py-2 border ${
                            errors.type ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          } rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent dark:bg-shoorah-darkBgColor dark:text-white disabled:opacity-50`}
                        >
                          <option value="">Select Type</option>
                          {types.map((type) => (
                            <option key={type.id || type._id} value={type.id || type._id}>
                              {type.name || type.typeName}
                            </option>
                          ))}
                        </select>
                        {errors.type && <p className="mt-1 text-sm text-red-500">{errors.type}</p>}
                      </div>

                      {/* Target Value and Rewards - Same Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Target Value */}
                        <div>
                          <label
                            htmlFor="targetValue"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                          >
                            Target Value <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            id="targetValue"
                            name="targetValue"
                            value={formData.targetValue}
                            onChange={handleInputChange}
                            min="1"
                            disabled={loader}
                            className={`w-full px-4 py-2 border ${
                              errors.targetValue
                                ? 'border-red-500'
                                : 'border-gray-300 dark:border-gray-600'
                            } rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent dark:bg-shoorah-darkBgColor dark:text-white disabled:opacity-50`}
                            placeholder="Enter target value"
                          />
                          {errors.targetValue && (
                            <p className="mt-1 text-sm text-red-500">{errors.targetValue}</p>
                          )}
                        </div>

                        {/* Rewards */}
                        <div>
                          <label
                            htmlFor="rewards"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                          >
                            Rewards <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            id="rewards"
                            name="rewards"
                            value={formData.rewards}
                            onChange={handleInputChange}
                            min="1"
                            disabled={loader}
                            className={`w-full px-4 py-2 border ${
                              errors.rewards
                                ? 'border-red-500'
                                : 'border-gray-300 dark:border-gray-600'
                            } rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent dark:bg-shoorah-darkBgColor dark:text-white disabled:opacity-50`}
                            placeholder="Enter reward points"
                          />
                          {errors.rewards && (
                            <p className="mt-1 text-sm text-red-500">{errors.rewards}</p>
                          )}
                        </div>
                      </div>

                      {/* Icon Selection - Full Width */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          Select Achievement Icon
                        </label>
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                          {ACHIEVEMENT_ICONS.map((iconOption) => (
                            <div
                              key={iconOption.value}
                              onClick={() => !loader && handleIconSelect(iconOption.value)}
                              className={`cursor-pointer border-2 rounded-lg p-2 transition-all hover:shadow-md ${
                                formData.icon === iconOption.value
                                  ? 'border-shoorah-primary bg-shoorah-primary/10 shadow-md'
                                  : 'border-gray-300 dark:border-gray-600 hover:border-shoorah-primary/50'
                              } ${loader ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={iconOption.title}
                            >
                              <img
                                src={iconOption.path}
                                alt={iconOption.title}
                                className="w-full h-auto object-contain"
                              />
                            </div>
                          ))}
                        </div>
                        {formData.icon && (
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            Selected:{' '}
                            {ACHIEVEMENT_ICONS.find((i) => i.value === formData.icon)?.title ||
                              'Custom'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end gap-4 px-6 py-4 border-t border-gray-200 dark:border-shoorah-darkBorder">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={loader}
                      className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loader}
                      className="btn-primary min-w-[140px]"
                    >
                      {loader ? (
                        <Spinner />
                      ) : editData ? (
                        'Update Achievement'
                      ) : (
                        'Create Achievement'
                      )}
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CreateAchievementModal;
